/**
 * RecordingPlaybackPlayer — the one HLS recording player, shared by both dashboards.
 *
 * Playing a recording on this system is not "point hls.js at a URL". Chunks are transcoded ON
 * DEMAND by the node, so the first request for a segment can legitimately take 40–70 seconds
 * before any bytes exist; a scrub abandons transcodes that then have to be cancelled; retention
 * deletes segments underneath a playing timeline; and the cloud adds a WebSocket tunnel on top of
 * all of it. Every one of those looks like "a chunk 404'd" to a naive player, and the naive
 * response — retry forever — produces the symptom this component exists to prevent: a permanent
 * "Buffering…" with no explanation.
 *
 * The error handling below was worked out against those failures on the local dashboard's player
 * and had NOT been carried over anywhere else, so the cloud silently lacked it. Having one
 * component means a lesson learned once applies everywhere.
 *
 * ─── The chunk-availability rules ───
 * Fragment failures are counted PER FRAGMENT over a 30-second window, and the budget depends on
 * what the server said, because the statuses mean genuinely different things:
 *
 *   404 / 410  the segment is gone — retention removed it, or its transcode failed.
 *              A few retries still cover "transcode is catching up", then it is permanent.
 *   503        the playback service is transcoding this chunk and says so. Long leash: it will
 *              stop saying so once the chunk lands. Treating this as permanent was what made a
 *              healthy transcode-in-progress read as "recording gone".
 *   409        the server cancelled that transcode because it believed this player seeked away.
 *              Normal while scrubbing — long leash, but NOT unlimited: a cancel loop (each
 *              request cancelling the other's transcode) otherwise buffers forever in silence.
 *   other      timeout / 502 / no status — the live-edge case. Middle budget.
 *
 * Any success on a fragment resets the streak, so only SUSTAINED failure on one chunk gives up.
 *
 * ─── hls.js ───
 * A peerDependency: both apps already ship it, so declaring it here shares their copy rather
 * than bundling a second one.
 */
import { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';

export interface RecordingPlaybackPlayerProps {
  /** HLS playlist URL, or a direct media URL. Null tears the player down. */
  src: string | null;
  /**
   * Bind the element to a ref the parent owns, so it can drive play/pause, seek and rate.
   * Omit it and the player manages its own element (with native controls if `controls`).
   */
  videoRef?: React.RefObject<HTMLVideoElement>;
  /** Play as soon as the manifest parses. */
  autoPlay?: boolean;
  /**
   * The caller's play/pause INTENT. hls.js refires `canplay` at every segment boundary, so
   * without this a video the user paused jumps back to playing at each discontinuity.
   */
  wantPlaying?: boolean;
  playbackRate?: number;
  /** Native browser controls. Off by default — most callers supply their own transport. */
  controls?: boolean;
  /**
   * No audio. A wall of six recordings playing at once wants silence — and a muted element is
   * also what browsers let a script start without a click on it.
   */
  muted?: boolean;
  /**
   * Longer timeouts, for a stream crossing the cloud's WebSocket tunnel. The local dashboard
   * talks to its own node and does not need them.
   */
  tunnelled?: boolean;
  /**
   * The bearer token for playlist and segment requests, read PER REQUEST.
   *
   * A callback, not a string, for two reasons. The app silently rotates its access token (~every
   * 30 minutes), so a value captured once at player setup goes stale and every later manifest or
   * fragment load 401s mid-session. And the two dashboards keep it under different keys
   * (`accessToken` vs `cloud_token`), which is the host's business, not this component's.
   *
   * Needed because hls.js fetches with its own XHR — it never passes through the app's axios
   * interceptor, so nothing else attaches the header.
   */
  getAuthToken?: () => string | null;
  /** Message the operator can act on. Null clears it. */
  onError?: (message: string | null) => void;
  onCanPlay?: () => void;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
  onEnded?: () => void;
  className?: string;
}

/** Fragment retry budgets, by what the failure means. See the header. */
const FRAG_BUDGET = { permanent: 6, transcoding: 25, cancelled: 25, transient: 15 };

/** A streak on one fragment older than this is a new problem, not a continuing one. */
const FRAG_WINDOW_MS = 30_000;

export default function RecordingPlaybackPlayer({
  src,
  videoRef: externalRef,
  autoPlay = false,
  wantPlaying = true,
  playbackRate = 1,
  controls = false,
  muted = false,
  tunnelled = false,
  getAuthToken,
  onError,
  onCanPlay,
  onTimeUpdate,
  onEnded,
  className = '',
}: RecordingPlaybackPlayerProps) {
  const internalRef = useRef<HTMLVideoElement>(null);
  const videoRef = externalRef || internalRef;
  const hlsRef = useRef<Hls | null>(null);

  const [buffering, setBuffering] = useState(false);

  /** Escalation counters for manifest and media errors, and when they were last touched. */
  const recoverRef = useRef({ manifest: 0, media: 0, ts: 0 });
  /** Repeated failures on ONE fragment — the chunk-availability signal. */
  const fragFailRef = useRef({ url: '', count: 0, ts: 0 });

  /**
   * Identifies this player to the node's playback service. Chunk requests carry it as `?sid=`,
   * which is how the server knows a seek abandoned the segment it was transcoding and can cancel
   * that job — otherwise a scrub leaves a queue of pointless transcodes hogging the GPU ahead of
   * the segment now wanted.
   */
  const sessionIdRef = useRef(`p${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`);

  // Kept in refs so the hls.js effect does not need them as dependencies — it must not tear the
  // stream down and rebuild it every time the parent's play intent or rate changes.
  const wantPlayingRef = useRef(wantPlaying);
  const rateRef = useRef(playbackRate);
  const getAuthTokenRef = useRef(getAuthToken);
  useEffect(() => { wantPlayingRef.current = wantPlaying; }, [wantPlaying]);
  useEffect(() => { rateRef.current = playbackRate; }, [playbackRate]);
  useEffect(() => { getAuthTokenRef.current = getAuthToken; }, [getAuthToken]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = playbackRate;
  }, [playbackRate, src, videoRef]);

  // Set as a property too: React's `muted` prop is not reliably applied to the element on first
  // render, and the autoplay policy reads the property.
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted, videoRef]);

  useEffect(() => {
    const video = videoRef.current;
    if (!src || !video) return undefined;

    onError?.(null);
    recoverRef.current = { manifest: 0, media: 0, ts: 0 };
    fragFailRef.current = { url: '', count: 0, ts: 0 };

    const isHls = src.includes('.m3u8') || src.includes('/playlist/');

    // Safari plays HLS natively and does NOT report Hls.isSupported(), so this is a real branch,
    // not a legacy fallback.
    if (!isHls || !Hls.isSupported()) {
      video.src = src;
      if (autoPlay) video.play().catch(() => { /* blocked by policy */ });
      return () => { video.removeAttribute('src'); try { video.load(); } catch { /* gone */ } };
    }

    const hls = new Hls({
      enableWorker: true,
      maxBufferLength: 30,
      maxMaxBufferLength: 60,
      backBufferLength: 30,
      // The first manifest request triggers server-side transcoding, which can take 10–30s
      // (longer through the tunnel). The library defaults time out well before that.
      manifestLoadingTimeOut: 60000,
      manifestLoadingMaxRetry: tunnelled ? 4 : 3,
      manifestLoadingRetryDelay: 2000,
      levelLoadingTimeOut: 60000,
      fragLoadingTimeOut: 60000,
      fragLoadingMaxRetry: 6,
      fragLoadingRetryDelay: 500,
      // Rapid scrubbing over on-demand-transcoded chunks creates buffer holes and stalls at
      // discontinuities — room to nudge past them rather than going fatal.
      maxBufferHole: 0.5,
      highBufferWatchdogPeriod: 1,
      nudgeOffset: 0.2,
      nudgeMaxRetry: 10,
      appendErrorMaxRetry: 5,
      xhrSetup: (xhr, url) => {
        // Tag chunk requests with this player's session id so the server can cancel the
        // transcodes a seek left behind. Opened EXPLICITLY because this is the one hook where
        // the URL can still be changed (hls.js only opens the request itself if xhrSetup did
        // not) — and it must happen before setRequestHeader below, or the header is discarded.
        if (url.includes('/hls/') && url.includes('.ts')) {
          const sep = url.includes('?') ? '&' : '?';
          xhr.open('GET', `${url}${sep}sid=${sessionIdRef.current}`, true);
        }
        // Read the token PER REQUEST — see getAuthToken. Without this the playlist request is
        // rejected with 401 "Missing or invalid authorization header", because hls.js fetches
        // outside the app's axios interceptor.
        const token = getAuthTokenRef.current?.();
        if (token && (url.includes('/api/') || url.includes('/playlist/'))) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
      },
    });

    hlsRef.current = hls;
    hls.loadSource(src);
    hls.attachMedia(video);

    const fail = (message: string) => {
      try { hls.destroy(); } catch { /* already gone */ }
      hlsRef.current = null;
      setBuffering(false);
      onError?.(message);
    };

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      onError?.(null);
      video.playbackRate = rateRef.current;
      if (autoPlay && wantPlayingRef.current) video.play().catch(() => { /* blocked by policy */ });
    });

    // Any successful fragment clears the streak, so only SUSTAINED failure on one chunk counts.
    hls.on(Hls.Events.FRAG_BUFFERED, () => {
      fragFailRef.current = { url: '', count: 0, ts: 0 };
      setBuffering(false);
    });

    hls.on(Hls.Events.ERROR, (_evt: any, data: any) => {
      const D = Hls.ErrorDetails;
      const recover = recoverRef.current;
      const status = data.response?.code || data.networkDetails?.status;
      const now = Date.now();

      if (!data.fatal) {
        // A stalled buffer is nudged, not reported. Scrubbing over transcoded chunks does this
        // routinely and recovers; surfacing it would make working playback look broken.
        if (data.details === D.BUFFER_STALLED_ERROR) {
          setBuffering(true);
          try { video.currentTime += 0.1; } catch { /* not seekable yet */ }
        }
        return;
      }

      if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
        const manifestFailure = data.details === D.MANIFEST_LOAD_ERROR
          || data.details === D.MANIFEST_LOAD_TIMEOUT
          || data.details === D.MANIFEST_PARSING_ERROR;

        if (manifestFailure) {
          // Distinguish WHY before giving up. An auth blip around the app's silent token
          // rotation deserves a retry — the next request picks up the fresh token. Only a
          // genuine 404/410, or persistent failure, is an error worth showing.
          if (now - recover.ts > 30_000) recover.manifest = 0;
          recover.ts = now;
          recover.manifest += 1;

          if ((status === 401 || status === 403 || !status) && recover.manifest <= 2) {
            setTimeout(() => { try { hls.startLoad(); } catch { /* torn down */ } }, 1500);
          } else {
            fail(status === 401 || status === 403
              ? 'Not authorised to play this recording — try reloading the page.'
              : 'This recording could not be loaded. It may no longer be available.');
          }
          return;
        }

        // ── Fragment failure: the chunk-availability case ──
        const fragUrl = data.frag?.url || data.context?.url || '';
        const f = fragFailRef.current;
        if (fragUrl && fragUrl === f.url && now - f.ts < FRAG_WINDOW_MS) f.count += 1;
        else { f.url = fragUrl; f.count = 1; }
        f.ts = now;

        const permanent = status === 404 || status === 410;
        const transcoding = status === 503;
        const cancelled = status === 409;
        const budget = permanent ? FRAG_BUDGET.permanent
          : transcoding ? FRAG_BUDGET.transcoding
            : cancelled ? FRAG_BUDGET.cancelled
              : FRAG_BUDGET.transient;

        if (f.count >= budget) {
          f.url = ''; f.count = 0;
          fail(permanent
            ? 'This part of the recording is unavailable — it may have been removed by the '
              + 'storage-retention policy, or the video failed to transcode.'
            : transcoding
              ? 'Still converting this part of the recording — it is taking longer than '
                + 'expected. Try again in a moment.'
              : 'Playback stalled — this part of the recording could not be loaded.');
          return;
        }

        // Within the transient budget: keep going, and say we are working on it.
        setBuffering(true);
        try { hls.startLoad(); } catch { /* torn down */ }
        return;
      }

      if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
        // The escalation hls.js documents: recover, then swap audio codec and recover, then stop.
        if (now - recover.ts > 5000) recover.media = 0;
        recover.ts = now;
        recover.media += 1;
        if (recover.media <= 1) hls.recoverMediaError();
        else if (recover.media === 2) {
          try { hls.swapAudioCodec(); } catch { /* not applicable */ }
          hls.recoverMediaError();
        } else fail('Playback error — please reload the page.');
        return;
      }

      fail(`Playback failed (${data.details || data.type}).`);
    });

    return () => {
      try { hls.destroy(); } catch { /* already gone */ }
      hlsRef.current = null;
      try { video.pause(); } catch { /* detached */ }
    };
    // onError/onCanPlay are intentionally excluded: an inline arrow from the parent would
    // otherwise rebuild the whole HLS pipeline on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, autoPlay, tunnelled]);

  // Element events, separate from the HLS lifecycle so a parent callback change does not tear
  // the stream down.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const onPlaying = () => setBuffering(false);
    const onWaiting = () => setBuffering(true);
    const onCanPlayEv = () => { setBuffering(false); onCanPlay?.(); };
    const onTime = () => onTimeUpdate?.(v.currentTime || 0, Number.isFinite(v.duration) ? v.duration : 0);
    const onEnd = () => onEnded?.();
    v.addEventListener('playing', onPlaying);
    v.addEventListener('waiting', onWaiting);
    v.addEventListener('canplay', onCanPlayEv);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('ended', onEnd);
    return () => {
      v.removeEventListener('playing', onPlaying);
      v.removeEventListener('waiting', onWaiting);
      v.removeEventListener('canplay', onCanPlayEv);
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('ended', onEnd);
    };
  }, [videoRef, onCanPlay, onTimeUpdate, onEnded]);

  return (
    <div className={`relative h-full w-full bg-black ${className}`}>
      <video
        ref={videoRef}
        className="h-full w-full bg-black"
        playsInline
        muted={muted}
        controls={controls}
      />
      {buffering && (
        // Shown only while a retry is genuinely in flight. Once the budget is spent the parent
        // gets a message instead — which is the whole point: a spinner that never resolves is
        // the failure this component was written to remove.
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="rounded bg-black/60 px-2 py-1 text-[11px] text-white">Buffering…</div>
        </div>
      )}
    </div>
  );
}
